import { Hotkey, HotkeysProvider } from "angular-hotkeys";

interface IZoomHotkeys {
  zoomInWithModAndPlus: Hotkey;
  zoomInWithModAndEquals: Hotkey;
  zoomOutWithModAndMinus: Hotkey;
}

interface IDraggableHotkeys {
  enableDraggableWithKey: Hotkey;
  enableDraggableWithSpace: Hotkey;
  enablePointerWithKey: Hotkey;
  enablePointerWithSpace: Hotkey;
}

interface ICommonHotkeys {
  selectAll?: Hotkey;
  copy?: Hotkey;
  cut?: Hotkey;
  undo?: Hotkey;
  redo?: Hotkey;
  delete?: Hotkey;
  backspace?: Hotkey;
  esc?: Hotkey;
}

export interface IZoomActions {
  zoomIn(event: Event, hotkey: Hotkey): void;
  zoomOut(event: Event, hotkey: Hotkey): void;
}

export interface IDraggableActions {
  enableDraggableWithKey(event: Event, hotkey: Hotkey): void;
  enablePointerWithKey(event: Event, hotkey: Hotkey): void;
  enableDraggableWithSpace(event: Event, hotkey: Hotkey): void;
  enablePointerWithSpace(event: Event, hotkey: Hotkey): void;
}

export interface ICommonActions {
  selectAll?(event: Event, hotkey: Hotkey): void;
  copy?(event: Event, hotkey: Hotkey): void;
  cut?(event: Event, hotkey: Hotkey): void;
  undo?(event: Event, hotkey: Hotkey): void;
  redo?(event: Event, hotkey: Hotkey): void;
  delete?(event: Event, hotkey: Hotkey): void;
  backspace?(event: Event, hotkey: Hotkey): void;
  esc?(event: Event, hotkey: Hotkey): void;
}

export function registerZoomHotkeys(hotkeysProvider: HotkeysProvider, zoomActions: IZoomActions): () => void {
  const registeredHotkeys: IZoomHotkeys = {
    zoomInWithModAndPlus: hotkeysProvider.add({
      combo: "mod++",
      callback: zoomActions.zoomIn,
    }),
    zoomInWithModAndEquals: hotkeysProvider.add({
      combo: "mod+=",
      callback: zoomActions.zoomIn,
    }),
    zoomOutWithModAndMinus: hotkeysProvider.add({
      combo: "mod+-",
      callback: zoomActions.zoomOut,
    }),
  };

  return () => {
    destroyRegisteredHotkeys(hotkeysProvider, registeredHotkeys);
  };
}

export function registerDraggableHotkeys(hotkeysProvider: HotkeysProvider, draggableActions: IDraggableActions): () => void {
  const registeredHotkeys: IDraggableHotkeys = {
    enableDraggableWithKey: hotkeysProvider.add({
      combo: "h",
      callback: draggableActions.enableDraggableWithKey,
    }),
    enablePointerWithKey: hotkeysProvider.add({
      combo: "v",
      callback: draggableActions.enablePointerWithKey,
    }),
    enablePointerWithSpace: hotkeysProvider.add({
      combo: "space",
      action: "keyup",
      callback: draggableActions.enablePointerWithSpace,
    }),
    enableDraggableWithSpace: hotkeysProvider.add({
      combo: "space",
      action: "keydown",
      callback: draggableActions.enableDraggableWithSpace,
    }),
  };

  return () => {
    destroyRegisteredHotkeys(hotkeysProvider, registeredHotkeys);
  };
}

export function registerCommonHotkeys(hotkeysProvider: HotkeysProvider, commonActions: ICommonActions): () => void {
  const registeredHotkeys: ICommonHotkeys = {};

  if (commonActions.selectAll) {
    registeredHotkeys.selectAll = hotkeysProvider.add({
      combo: "mod+a",
      callback: commonActions.selectAll,
    });
  }
  if (commonActions.copy) {
    registeredHotkeys.copy = hotkeysProvider.add({
      combo: "mod+c",
      callback: commonActions.copy,
    });
  }
  if (commonActions.cut) {
    registeredHotkeys.cut = hotkeysProvider.add({
      combo: "mod+x",
      callback: commonActions.cut,
    });
  }
  if (commonActions.undo) {
    registeredHotkeys.undo = hotkeysProvider.add({
      combo: "mod+z",
      callback: commonActions.undo,
    });
  }
  if (commonActions.redo) {
    registeredHotkeys.redo = hotkeysProvider.add({
      combo: "shift+mod+z",
      callback: commonActions.redo,
    });
  }
  if (commonActions.delete) {
    registeredHotkeys.delete = hotkeysProvider.add({
      combo: "del",
      callback: commonActions.delete,
    });
  }
  if (commonActions.backspace) {
    registeredHotkeys.backspace = hotkeysProvider.add({
      combo: "backspace",
      callback: commonActions.backspace,
    });
  }
  if (commonActions.esc) {
    registeredHotkeys.esc = hotkeysProvider.add({
      combo: "esc",
      callback: commonActions.esc,
    });
  }

  return () => {
    destroyRegisteredHotkeys(hotkeysProvider, registeredHotkeys);
  };
}

function destroyRegisteredHotkeys(hotkeysProvider: HotkeysProvider, registeredHotkeys: IZoomHotkeys | IDraggableHotkeys | ICommonHotkeys) {
  for (const key in registeredHotkeys) {
    hotkeysProvider.del(registeredHotkeys[key]);
    delete registeredHotkeys[key];
  }
}
